import React, { useState, useEffect } from 'react';
import { MessageSquare, Star } from 'lucide-react';
import { feedbackTypes } from '../data/mock';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';

const Feedback = () => {
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    feedbackType: '',
    rating: 0,
    message: '',
  });
  const [hoverRating, setHoverRating] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.feedbackType || !formData.message) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in feedback type and message.',
        variant: 'destructive',
      });
      return;
    }
    toast({
      title: 'Feedback Submitted!',
      description: 'Thank you for your feedback!',
    });
    setFormData({ name: '', email: '', feedbackType: '', rating: 0, message: '' });
  };

  return (
    <div className="pt-24 pb-20 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-12 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <MessageSquare className="w-16 h-16 text-cyan-400 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Share Your Feedback
          </h1>
          <p className="text-xl text-gray-300">
            Your feedback helps me improve my services
          </p>
        </div>

        {/* Form */}
        <div
          className={`transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="rounded-xl border bg-gradient-to-br from-stone-800/50 via-neutral-800/40 to-slate-800/50 border-teal-500/40 backdrop-blur-xl shadow-2xl shadow-teal-500/30">
            <div className="p-6">
              <h2 className="font-semibold tracking-tight text-2xl text-white">
                Feedback Form
              </h2>
              <p className="text-sm text-gray-300 mt-1">
                All fields are optional except feedback type and message
              </p>
            </div>
            <div className="p-6 pt-0">
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-white block mb-1.5">
                      Name (Optional)
                    </label>
                    <input
                      className="flex h-9 w-full rounded-md border px-3 py-1 text-sm shadow-sm bg-slate-800/50 border-slate-600 text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white block mb-1.5">
                      Email (Optional)
                    </label>
                    <input
                      type="email"
                      className="flex h-9 w-full rounded-md border px-3 py-1 text-sm shadow-sm bg-slate-800/50 border-slate-600 text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-white block mb-1.5">
                    Feedback Type *
                  </label>
                  <Select
                    value={formData.feedbackType}
                    onValueChange={(val) =>
                      setFormData({ ...formData, feedbackType: val })
                    }
                  >
                    <SelectTrigger className="w-full bg-slate-800/50 border-slate-600 text-white">
                      <SelectValue placeholder="Select feedback type" />
                    </SelectTrigger>
                    <SelectContent>
                      {feedbackTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Rating (Optional)
                  </label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className="transition-transform hover:scale-110"
                        onClick={() =>
                          setFormData({ ...formData, rating: star })
                        }
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                      >
                        <Star
                          className={`w-8 h-8 transition-colors ${
                            star <= (hoverRating || formData.rating)
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-500'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-white block mb-1.5">
                    Your Feedback *
                  </label>
                  <textarea
                    className="flex min-h-[60px] w-full rounded-md border px-3 py-2 text-sm shadow-sm bg-slate-800/50 border-slate-600 text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                    placeholder="Share your thoughts, suggestions, or experience..."
                    required
                    rows={6}
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                  />
                </div>

                <button
                  className="inline-flex items-center justify-center gap-2 text-sm font-medium rounded-md px-8 w-full py-3 bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-600 hover:from-teal-500 hover:via-cyan-500 hover:to-teal-500 text-white shadow-lg shadow-teal-500/50 hover:shadow-xl transition-all duration-300 hover:scale-105"
                  type="submit"
                >
                  <MessageSquare className="w-5 h-5" />
                  Submit Feedback
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
